import { asyncScheduler, combineLatest, map, observeOn, of } from "rxjs";
import { createListStore, UseListStore } from "./listStore";
import { useDefaultValues } from "./defaultValues";
import { allLanguages, formatNumber, getLanguageFromLandCode, getResource, useLanguageStore, useResourcesStore } from "./localization";
import { UserTypeId } from "./account";
import { useConfig } from "./config";
import { getLanguages } from "./utils";

export interface EnumItemDto {
    id: number | string;
    code?: number | string | null;
    name: string;
    inactive?: boolean;
    parentId?: number;
}

export enum CustomerStateId {
    Active = 1,
    Passive = 2,
    BadPayer = 3,
    G = 4,
    Dead = 5,
}

export enum CustomerSexTypeId {
    Unknown = 1,
    Male = 2,
    Female = 3,
}

export enum OrderStateId {
    ReshipMulti = 1,
    NoShipment = 2,
    Active = 3,
    ReturnFirst = 4,
    ReturnOther = 5,
    Passive = 6,
    DelStopped = 7,
    WoTransfer = 8,
    WoOnly = 9,
    Shipped = 10,
    StoppedArko = 11,
    ReturnFirstRs = 12,
}

export enum InvoiceStateId {
    NotShipped = 2,
    Open = 3,
    Dunned = 6,
}

export enum CustomerNoteTypeId {
    System = 8,
}

export enum CustomerNoteActionTypeId {
    None = 0,
}

export enum RetentionCodeId {
    FreePack = 20,
    DecreaseSu = 21,
    IncreaseSu = 22,
    MailOrder = 23,
    SkipEachSecondShp = 24,
    Discount = 25,
    FreeGift = 26,
    NewContOrder = 27,
    SkipCycle = 28,
    ComplaintResolved = 29,
    Failure = 30,
    NewWP = 31,
    AddressChange = 32,
    DueDateChange = 33,
    DelayedCancelation = 34,
}

export enum PaymentMethodId {
    Cred = 1,
    Cod = 2,
    Card = 3,
}

export enum OfferTypeId {
    None = 0,
    SuChange = 1,
    NewCo = 2,
    NewMo = 3,
}

export const offerTypes: EnumItemDto[] = [
    { id: OfferTypeId.None, name: "(none)" },
    { id: OfferTypeId.SuChange, name: "SU change" },
    { id: OfferTypeId.NewCo, name: "New continuity order" },
    { id: OfferTypeId.NewMo, name: "New MO order" },
];

export const moOfferTypes: EnumItemDto[] = [
    { id: OfferTypeId.None, name: "(none)" },
    { id: OfferTypeId.NewCo, name: "New continuity order" },
    { id: OfferTypeId.NewMo, name: "New MO order" },
];

export const orderTypes: EnumItemDto[] = [
    { id: "M", name: "M" },
    { id: "C", name: "C" },
];

export enum LogEntryType {
    Info = 1,
    Warning = 2,
    Error = 3,
    Exception = 4,
    Result = 5,
}

export const logEntryTypes: EnumItemDto[] = [
    { id: LogEntryType.Info, name: "Info" },
    { id: LogEntryType.Warning, name: "Warning" },
    { id: LogEntryType.Error, name: "Error" },
    { id: LogEntryType.Exception, name: "Exception" },
    { id: LogEntryType.Result, name: "Result" },
];

export const MailProductCode = "MAIL";

export interface OfferMailOrderShipmentEnumDto {
    id: number;
    name: string;
    btoAmt: number;
}

export enum DeliveryMethodId {
    Zasilkovna = 1,
    Gls = 2,
    SmartPosti = 3,
    APost = 4,
    Dpd = 5
}

export interface DeliveryMethodDto extends EnumItemDto {
    isDefault: boolean;
    hasPlaces: boolean;
    hasMoProjects: boolean;
    hasCoProjects: boolean;
    isCodPickupPointAllowed: boolean;
    pickUpPointWeightLimit?: number;
    homeDeliveryWeightLimit?: number;
}

export enum CustomerFlagId {
    MailStop_No = 1,
    EmailStop_No = 2,
    PhoneStop_No = 3,
    MailStop_Yes = 4,
    EmailStop_Yes = 5,
    PhoneStop_Yes = 6,
    MailStop_GdprOk = 7,
    EmailStop_GdprOk = 8,
    PhoneStop_GdprOkTelSms = 9,
    PhoneStop_GdprTel = 10,
    PhoneStop_GdprSms = 11,

    MailStatus_None = 12,
    MailStatus_WrongAddress = 15,

    EmailStatus_None = 13,
    EmailStatus_WrongEmail = 17,

    TelStatus_None = 14,
    TelStatus_WrongNumber = 18,
    TelStatus_DuplicateNumber = 19,
    TelStatus_OrdersHimself = 20,
    TelStatus_NoPet = 21, // Unused, inactive

    EmailStopSystem_None = 23,
    EmailStopSystem_MultiNonOpener = 26,
    LoserStatus_None = 24,
    LoserStatus_Loser = 27,
    LoserStatus_HeavyLoser = 28,
    LoserStatus_BlackList = 29,
    LoserStatus_TooHighReturns = 30,
    LoserStatus_SuperLoser = 31,
    LoserStatus_UltraLoser = 34,

    MailStatus_NoPet = 32,
    EmailStatus_NoPet = 33, // Unused, inactive

    MobStatus_None = 35,
    MobStatus_WrongNumber = 36,
    MobStatus_DuplicateNumber = 37,
    MobStatus_OrdersHimself = 38,
    MobStatus_NoPet = 39, // Unused, inactive
}

export enum CustomerFlagType {
    MailStop = 1,
    EmailStop = 2,
    PhoneStop = 3,
    MailStatus = 4,
    EmailStatus = 5,
    TelStatus = 6,
    MobStatus = 7,
    EmailStopSystem = 8,
    LoserStatus = 9,
}

export const customerFlagTypes = [
    { id: CustomerFlagType.MailStop, name: "Mail stop" },
    { id: CustomerFlagType.EmailStop, name: "Email stop" },
    { id: CustomerFlagType.PhoneStop, name: "Phone stop" },
    { id: CustomerFlagType.MailStatus, name: "Mail status" },
    { id: CustomerFlagType.EmailStatus, name: "Email status" },
    { id: CustomerFlagType.TelStatus, name: "Phone status" },
    { id: CustomerFlagType.MobStatus, name: "Mobile status" },
    { id: CustomerFlagType.EmailStopSystem, name: "Email stop system" },
    { id: CustomerFlagType.LoserStatus, name: "Loser status" },
];

export interface CustomerStopCodeEnumDto {
    id: CustomerFlagId;
    code: string;
    charCode: string;
    name: string;
    inactive: boolean;
    type: CustomerFlagType;
}

export enum PackageTypeId {
    SmallPackage = 1,
    Package = 2,
    Gift = 3
}

export const packagesTypes: EnumItemDto[] = [
    { id: PackageTypeId.SmallPackage, name: "Small package" },
    { id: PackageTypeId.Package, name: "Package" },
    { id: PackageTypeId.Gift, name: "Gift" },
];

export interface InventoryArticleEnumItemDto extends EnumItemDto {
    isPackage: boolean;
}

export const tmUserTypeIds = [UserTypeId.Admin, UserTypeId.TmSupervisor, UserTypeId.TmManager, UserTypeId.TmAgent, UserTypeId.Supervisor, UserTypeId.User];
export const onlyTmUserTypeIds = [UserTypeId.TmSupervisor, UserTypeId.TmAgent, UserTypeId.TmManager];
export const onlyTmUserTypeIdsForSupervisor = [UserTypeId.TmSupervisor, UserTypeId.TmAgent];

export const useCustomerStates = createListStore<EnumItemDto>({ path: "enums/customerStates" });
export const useCustomerSexTypes = createListStore<EnumItemDto>({ path: "enums/customerSexTypes" });
export const useCustomerFlags = createListStore<CustomerStopCodeEnumDto>({ path: "enums/customerFlags" });
export const useCustomerFlagsEnum = createListStore<EnumItemDto>({ data: useCustomerFlags.getState().items$ });
export const useOrderStates = createListStore<EnumItemDto>({ path: "enums/orderStates" });
export const useOrderStatesLocalized = localizeEnum(useOrderStates, "Ui.OrderState.");
export const useInvoiceStates = createListStore<EnumItemDto>({ path: "enums/invoiceStates" });
export const usePaymentMethods = createListStore<EnumItemDto>({ path: "enums/paymentMethods" });
export const useCustomerNoteTypes = createListStore<EnumItemDto>({ path: "enums/customerNoteTypes" });
export const useCustomerNoteActionTypes = createListStore<EnumItemDto>({ path: "enums/customerNoteActionTypes" });
export const useWarehouses = createListStore<EnumItemDto>({ path: "enums/warehouses" });

export const useEPrefContentTypes = createListStore<EnumItemDto>({ path: "enums/ePrefContent" });
export const useEPrefFrequencies = createListStore<EnumItemDto>({ path: "enums/ePrefFrequency" });
export const useEPrefTypes = createListStore<EnumItemDto>({ path: "enums/ePrefType" });

export const useInventoryArticlesAllRaw = createListStore<InventoryArticleEnumItemDto>({ path: "enums/inventoryArticles" });
export const useArticles = createListStore<EnumItemDto>({ data: useInventoryArticlesAllRaw.getState().items$ });
export const useInventoryArticles = createListStore<EnumItemDto>({
    data: useInventoryArticlesAllRaw.getState().items$.pipe(map(p =>
        ({ items: p.items.filter(p => !p.isPackage), isFetching: p.isFetching })
    ))
});
export const usePackages = createListStore<EnumItemDto>({
    data: useInventoryArticlesAllRaw.getState().items$.pipe(map(p =>
        ({ items: p.items.filter(p => p.isPackage), isFetching: p.isFetching })
    ))
});

export const useInventoryArticlesAllWithCodeRaw = createListStore<InventoryArticleEnumItemDto>({ path: "enums/inventoryArticles/withCode" });
export const useArticlesWithCode = createListStore<EnumItemDto>({ data: useInventoryArticlesAllWithCodeRaw.getState().items$ });
export const useInventoryArticlesWithCode = createListStore<EnumItemDto>({
    data: useInventoryArticlesAllWithCodeRaw.getState().items$.pipe(map(p =>
        ({ items: p.items.filter(p => !p.isPackage), isFetching: p.isFetching })
    ))
});
export const usePackagesWithCode = createListStore<EnumItemDto>({
    data: useInventoryArticlesAllWithCodeRaw.getState().items$.pipe(map(p =>
        ({ items: p.items.filter(p => p.isPackage), isFetching: p.isFetching })
    ))
});

export const useTransactionCodes = createListStore<EnumItemDto>({ path: "enums/transactionCodes" });
export const useTransactionCodesWithCode = createListStore<EnumItemDto>({
    data: useTransactionCodes.getState().items$.pipe(map(p => ({
        items: p.items.map(p => ({ ...p, name: `${p.code} ${p.name}` })),
        isFetching: p.isFetching
    })))
});
export const usePrintTemplates = createListStore<EnumItemDto>({ path: "enums/printTemplate" });
export const useCollectionAgencies = createListStore<EnumItemDto>({ path: "enums/collectionAgencies" });
export const useOfferMailOrderShipments = createListStore<OfferMailOrderShipmentEnumDto>({ path: "enums/offerMailOrderShipments" });
export const useOfferMailOrderShipmentsWithBtoAmt = createListStore<EnumItemDto>({
    data: combineLatest([useOfferMailOrderShipments.getState().items$, useDefaultValues.getState().clientData$]).pipe(map(p => ({
        items: p[0].items.map(r => ({ id: r.id, name: `${r.id} - ${r.name} - ${formatNumber(r.btoAmt, p[1].data.landCode, true)}` })),
        isFetching: p[0].isFetching || p[1].isFetching,
    }))),
});
export const useOfferTypes = createListStore<EnumItemDto>({ data: of({ items: offerTypes, isFetching: false }) });
export const useLogEntrySeverities = createListStore<EnumItemDto>({ data: of({ items: logEntryTypes, isFetching: false }) });
export const useUserTypes = createListStore<EnumItemDto>({ path: "enums/userTypes" });
export const useTmUserTypes = createListStore<EnumItemDto>({
    data: useUserTypes.getState().items$.pipe(map(p => ({
        items: p.items.filter(p => onlyTmUserTypeIds.includes(p.id as number)),
        isFetching: p.isFetching,
    }))),
});
export const useTmUserTypesForSupervisor = createListStore<EnumItemDto>({
    data: useUserTypes.getState().items$.pipe(map(p => ({
        items: p.items.filter(p => onlyTmUserTypeIds.includes(p.id as number)),
        isFetching: p.isFetching,
    }))),
});
export const useLogEntryTypes = createListStore({ data: of({ items: logEntryTypes, isFetching: false }) });
export const useDeliveryMethods = createListStore<DeliveryMethodDto>({ path: "enums/deliveryMethods" });
export const useCustomerFlagTypes = createListStore<EnumItemDto>({ data: of({ items: customerFlagTypes, isFetching: false }) });
export const useCallCenters = createListStore<EnumItemDto>({ path: "enums/callCenters" });
export const useTmCallResults = createListStore<EnumItemDto>({ path: "enums/tmCallResults" });
export const useLanguages = createListStore<EnumItemDto>({
    data: useConfig.getState().clientData$.pipe(map(p => {
        const localLanguage = getLanguageFromLandCode(p.data.landCode);
        return { items: allLanguages.map(p => ({ id: p, name: p, inactive: p !== "en" && p !== localLanguage })), isFetching: p.isFetching };
    }))
});

export const useYesNo = createListStore<EnumItemDto>({
    data: useResourcesStore.getState().clientData$.pipe(map(p => {
        return { items: [{ id: String(true), name: getResource("Ui.Yes", "Yes") as string }, { id: String(false), name: getResource("Ui.No", "No") as string }], isFetching: p.isFetching }
    })),
});

export const useClientLanguages = createListStore<EnumItemDto>({
    data: useDefaultValues.getState().clientData$.pipe(map(p => {
        const languages = getLanguages(p.data);
        return { items: languages, isFetching: p.isFetching };
    }))
});

export function localizeEnum(useEnumStore: UseListStore<EnumItemDto>, prefix: string) {
    return createListStore<EnumItemDto>({
        data: combineLatest([
            useLanguageStore.getState().language$,
            useResourcesStore.getState().clientData$,
            useEnumStore.getState().items$,
        ]).pipe(observeOn(asyncScheduler), map(([language, resources, enu]) => {
            return {
                items: enu.items.map(p => ({ ...p, name: getResource(prefix + p.id, p.name, language, resources.data) as string })),
                isFetching: enu.isFetching || resources.isFetching,
            };
        })),
    });
}
